/*
  Вкладка «Файлы» - вариант Б макетов, выбранный владельцем 30.09.2026.

  Сверху - куда прислать файлы, под ним - плитки обязательного минимума, итог по
  счетам с кнопкой переноса, спорные счета, расхождения с портфелем, месяцы с
  начислениями из двух источников (01.10.2026) и таблица присланных файлов.
  Контейнер: состояние и запросы - в `useUploads`, разметка - в блоках.
  Каждое действие показывает свою ошибку рядом с собой.

  Пока идёт перенос, действия, меняющие сведения файлов (выбор в споре,
  расхождения, сверка страницы, удаление), выключены с подсказкой
  (`TransferLock`, 01.10.2026): сервер всё равно отказал бы им (409).
*/

import { useCallback, useEffect, useRef, useState } from 'react'

import { api } from '../../api/client.js'
import { Lock } from '../AccessContext.jsx'
import { Confirm, Loading, Notice } from '../Common.jsx'
import DisputeList from './DisputeList.jsx'
import Divergences from './Divergences.jsx'
import FileDrop from './FileDrop.jsx'
import FoundSettings from './FoundSettings.jsx'
import MinimumTiles from './MinimumTiles.jsx'
import MonthChoices from './MonthChoices.jsx'
import ScanReview, { UnsavedConfirm } from './ScanReview.jsx'
import TransferLock from './TransferLock.jsx'
import TransferPanel from './TransferPanel.jsx'
import UploadTable from './UploadTable.jsx'
import useFoundSettings from './useFoundSettings.js'
// `revisionOf` - когда перечитывать расхождения с портфелем и месяцы из двух
// источников; тем же правилом хук перечитывает сводку, поэтому оно живёт там.
import useUploads, { revisionOf } from './useUploads.js'

const IDLE = { busy: null, error: null, place: null }

/* Пока сводка считается, файлы и поле выбора уже на экране (02.10.2026).
   Заголовок плиток стоит сразу: человек видит, где появится итог, а не серую
   строку над таблицей. Отказ без единого ответа - ошибкой с тем, что делать. */
function SummaryPending({ error }) {
  return (
    <section className="section">
      <h2 className="section__title">Обязательный минимум: по скольким счетам есть</h2>
      {error ? <Notice error={error} /> : <Loading what="Считаем, что собрано по счетам" />}
    </section>
  )
}

/*
  Сбой опроса поверх имеющихся данных (01.10.2026, проверка перед выкладкой).
  Раньше ошибка была видна, только пока данных не было вовсе, и человек смотрел
  на «перенос идёт», которое никто не обновлял. Запрос повторяется сам
  (`useUploads`), поэтому это предупреждение, а не красная ошибка.
*/
function RefreshTrouble({ error }) {
  if (!error) return null
  return (
    <Notice kind="warn">
      <div className="notice__what">Не удалось обновить вкладку: {error.message}</div>
      <div className="notice__do">
        {error.remedy} Арбитр ЖКХ повторит запрос сам, а пока на экране последние полученные сведения.
      </div>
    </Notice>
  )
}

export default function FilesTab({ onTransferred, transferMemory }) {
  const { data, stamp, error, summary, summaryError, transferring, reload } = useUploads()
  // Карточки сведений организации - по отпечатку ВСЕХ файлов: список таких файлов отбирает
  // сервер (21.10.2026), таблица показывает лишь одну страницу.
  const settings = useFoundSettings(data ? revisionOf(data) : null)
  const [work, setWork] = useState(IDLE)
  const [doomed, setDoomed] = useState(null)
  const [reviewing, setReviewing] = useState(null)
  const [sent, setSent] = useState(null)
  const [switching, setSwitching] = useState(null)

  /*
    Чек-лист и журнал загрузок раздела обновляются, когда перенос КОНЧИЛСЯ, а не
    когда нажата кнопка: с 01.10.2026 кнопка только ставит его в очередь.

    Память «перенос шёл» живёт у страницы раздела (`transferMemory`), а не у
    вкладки (01.10.2026, проверка перед выкладкой): вкладка разбирается при
    уходе на соседнюю, и перенос, кончившийся без неё, чек-лист не обновлял до
    перезагрузки страницы. Пока первый ответ не пришёл, о переносе ничего не
    известно - и память не трогается.
  */
  const ownMemory = useRef(false)
  const wasTransferring = transferMemory ?? ownMemory
  const known = Boolean(data)
  useEffect(() => {
    if (!known) return
    if (wasTransferring.current && !transferring) onTransferred?.()
    wasTransferring.current = transferring
  }, [known, transferring, onTransferred, wasTransferring])

  // Есть ли несохранённые правки в открытой сверке скана - чтобы спросить,
  // прежде чем открыть другой файл (01.10.2026).
  const reviewDirty = useRef(false)
  const markDirty = useCallback((dirty) => {
    reviewDirty.current = dirty
  }, [])
  // Открытый на сверку файл хранится строкой (`{ id, filename }`), а не берётся из списка:
  // список с 21.10.2026 - одна страница, и файл со страницы мог уйти.
  const openReview = (file) =>
    reviewing && file.id !== reviewing.id && reviewDirty.current ? setSwitching(file) : setReviewing(file)

  const act = async (place, action) => {
    setWork({ busy: place, error: null, place })
    try {
      await action()
      await reload()
      setWork(IDLE)
    } catch (failure) {
      setWork({ busy: null, error: failure, place })
    }
  }

  const errorAt = (place) => (work.place === place ? work.error : null)

  if (!data) return error ? <Notice error={error} /> : <Loading what="Загружаем присланные файлы" />

  return (
    <>
      <RefreshTrouble error={error} />
      <Lock>
        <FileDrop
          busy={work.busy === 'send'}
          error={errorAt('send')}
          sent={sent}
          onFiles={(files) =>
            act('send', async () => {
              setSent(null)
              setSent(await api.sendUploads(files))
            })
          }
        />
      </Lock>
      {summary ? <MinimumTiles summary={summary} /> : <SummaryPending error={summaryError} />}
      {/* Панель - сразу: ход переноса сводки не ждёт, числа встают, когда она пришла. */}
      <TransferPanel
        summary={summary}
        transfer={data.last_transfer}
        busy={work.busy === 'transfer'}
        error={errorAt('transfer')}
        onTransfer={(force, only) => act('transfer', () => api.transferUploads(force, only))}
      />
      <DisputeList count={summary?.conflicts ?? 0} onChosen={reload} locked={transferring} />
      <Divergences revision={revisionOf(data)} onDecided={reload} locked={transferring} />
      <MonthChoices revision={revisionOf(data)} onDecided={reload} locked={transferring} />
      <Notice error={settings.error} />
      {settings.files.map((file) => (
        <FoundSettings
          key={file.id}
          file={file}
          state={settings.found[file.id]}
          onApply={(choice) => settings.apply(file.id, choice)}
        />
      ))}
      {reviewing ? (
        <ScanReview
          key={reviewing.id}
          file={reviewing}
          locked={transferring}
          onDecided={reload}
          onDirty={markDirty}
          onClose={() => setReviewing(null)}
        />
      ) : null}
      <UploadTable
        stamp={stamp}
        busy={Boolean(work.busy)}
        locked={transferring}
        onDelete={setDoomed}
        onReview={openReview}
      />
      {switching ? (
        <UnsavedConfirm
          onLeave={() => {
            setReviewing(switching)
            setSwitching(null)
          }}
          onStay={() => setSwitching(null)}
        />
      ) : null}
      {doomed ? (
        <Confirm
          title="Удалить файл?"
          action="Удалить"
          busy={work.busy === 'delete'}
          error={errorAt('delete')}
          onCancel={() => {
            setDoomed(null)
            // Отмена закрывает окно и стирает только ошибку удаления: чужая
            // ошибка, например переноса, остаётся у своей кнопки (01.10.2026).
            setWork((current) => (current.place === 'delete' ? IDLE : current))
          }}
          onConfirm={() =>
            act('delete', async () => {
              await api.deleteUpload(doomed.id)
              // Сверка удалённого файла закрывается: сверять в нём больше нечего.
              setReviewing((current) => (current?.id === doomed.id ? null : current))
              setDoomed(null)
            })
          }
        >
          «{doomed.filename}» будет удалён вместе со сведениями, которые ещё не перенесены в
          портфель. Перенесённое останется в портфеле.
          <TransferLock locked={transferring} />
        </Confirm>
      ) : null}
    </>
  )
}
